import type { OverviewDto } from '../../../shared/api';
import { esc, money } from '../format';
import { topBar } from './header';
import { vMonthBanner } from './month';
import { categoryItems, yearlyLines } from './items';
import { budgetHeadline, leftToBudget, leftToBudgetText, vIncomeBudget } from './income';
import { groupShare } from '../../../shared/groups';
import type { GroupDto } from '../../../shared/api';

type Category = OverviewDto['categories'][number];

/**
 * Categories by group, in screen order. A category with no group (data from before groups) joins
 * the first group of its kind.
 */
export function groupedCategories(d: OverviewDto): { group: GroupDto; cats: Category[] }[] {
  const blocks = d.groups.map((group) => ({ group, cats: d.categories.filter((k) => k.groupId === group.id) }));
  for (const k of d.categories) {
    if (k.groupId && d.groups.some((g) => g.id === k.groupId)) continue;
    blocks.find((b) => b.group.kind === k.kind)?.cats.push(k);
  }
  return blocks;
}

/** The bits of the page refreshBudgetNumbers touches (the document, or a stand-in in tests). */
interface NumberEl {
  textContent: string | null;
  classList: { toggle(name: string, on?: boolean): unknown };
}
export interface NumbersDoc {
  getElementById(id: string): NumberEl | null;
  querySelector(selector: string): NumberEl | null;
}

/**
 * After an amount changes on the Budget tab: update the pinned headline, each itemized category's
 * total, each group's total and share, and the planned total in place, without redrawing the field
 * being edited.
 */
export function refreshBudgetNumbers(d: OverviewDto, doc: NumbersDoc = (globalThis as unknown as { document: NumbersDoc }).document) {
  const totals = budgetTotals(d);
  const ltb = doc.getElementById('ltb');
  if (ltb) {
    const cents = leftToBudget(d, totals);
    ltb.textContent = leftToBudgetText(cents);
    ltb.classList.toggle('neg', cents < 0);
  }
  for (const k of d.categories) {
    const tot = doc.querySelector(`[data-ktot="${k.id}"]`);
    if (tot) tot.textContent = money(k.budgetCents);
  }
  const all = doc.getElementById('ball');
  if (all) all.textContent = money(totals.spending + totals.setAside);
  for (const b of groupedCategories(d)) {
    const total = groupTotal(b.cats);
    const tot = doc.querySelector(`[data-gtot="${b.group.id}"]`);
    if (tot) tot.textContent = money(total);
    const share = doc.querySelector(`[data-gshare="${b.group.id}"]`);
    if (share) {
      const s = groupShare(total, d.totals.incomeCents, b.group.targetMinPct, b.group.targetMaxPct);
      share.textContent = s.text ?? '';
      share.classList.toggle('warn', s.warning);
    }
  }
}

/** A group's planned total: the same numbers its categories show. */
export const groupTotal = (cats: Category[]) => cats.reduce((s, k) => s + k.budgetCents, 0);

/**
 * The group's one line under its name, in plain words (`groupShare`): its percent of income and
 * how that sits against its target. Amber outside the target, muted otherwise.
 */
export function shareLine(d: OverviewDto, g: GroupDto, plannedCents: number): string {
  const share = groupShare(plannedCents, d.totals.incomeCents, g.targetMinPct, g.targetMaxPct);
  return `<span class="gshare ${share.warning ? 'warn' : ''}" data-gshare="${esc(g.id)}">${share.text ? esc(share.text) : ''}</span>`;
}

/** The drag handle (grip): long-press and drag, or focus it and use the arrow keys. */
export const grip = (what: 'category' | 'group', id: string, name: string) =>
  `<button class="grip" data-grip="${what}" data-id="${esc(id)}" aria-label="Reorder ${esc(name)}. Long-press and drag, or use the arrow keys." title="Drag to reorder"><svg viewBox="0 0 12 18" aria-hidden="true"><circle cx="3" cy="3" r="1.6"/><circle cx="9" cy="3" r="1.6"/><circle cx="3" cy="9" r="1.6"/><circle cx="9" cy="9" r="1.6"/><circle cx="3" cy="15" r="1.6"/><circle cx="9" cy="15" r="1.6"/></svg></button>`;

/** One group: a header (grip, fold, name, total, target, share, options) and, unfolded, its categories and Add category. */
function groupBlock(d: OverviewDto, b: { group: GroupDto; cats: Category[] }, row: (k: Category) => string): string {
  const g = b.group;
  const total = groupTotal(b.cats);
  const setAside = g.kind === 'set_aside';
  // Every new category starts as one amount, money set aside too.
  const add = setAside
    ? `<div class="addcat"><input id="newcat-${esc(g.id)}" type="text" placeholder="New savings or investing category" aria-label="New category in ${esc(g.name)}" maxlength="60"><input class="n" id="newbud-${esc(g.id)}" inputmode="decimal" placeholder="$0" aria-label="Monthly amount to move"><button class="btn" data-a="addcat" data-group="${esc(g.id)}">Add category</button></div>`
    : `<div class="addcat"><input id="newcat-${esc(g.id)}" type="text" placeholder="New category" aria-label="New category in ${esc(g.name)}" maxlength="60"><input class="n" id="newbud-${esc(g.id)}" inputmode="decimal" placeholder="$0" aria-label="Monthly amount"><button class="btn" data-a="addcat" data-group="${esc(g.id)}">Add category</button></div>`;
  return `<section class="gblock ${g.collapsed ? 'folded' : ''}" data-group="${esc(g.id)}" data-kind="${g.kind}" data-count="${b.cats.length}">
  <div class="ghead">${grip('group', g.id, g.name)}
    <button class="gname" data-a="group-toggle" data-id="${esc(g.id)}" aria-expanded="${!g.collapsed}"><span class="fold" aria-hidden="true">${g.collapsed ? '&#9656;' : '&#9662;'}</span> ${esc(g.name)}</button>
    <span class="gtot" data-gtot="${esc(g.id)}">${money(total)}</span>
  </div>
  <div class="gsub">${shareLine(d, g, total)}<span class="gacts"><button class="mini" data-a="group-options" data-id="${esc(g.id)}">Options</button></span></div>
  ${
    g.collapsed
      ? ''
      : `<div class="list cats">${b.cats.length ? b.cats.map(row).join('') : `<p class="hint">${setAside ? 'Savings and investing you move by hand each month, like a Roth IRA or travel savings.' : 'No categories yet.'}</p>`}</div>
  ${add}`
  }
</section>`;
}

/** An inline amount: saves on leaving the field or Enter (main.ts wires it), "Saved" shows beside it. */
export const inlineAmount = (kind: 'budget' | 'extra' | 'item', id: string, cents: number, label: string, tag = '') =>
  `<div class="amtcell">${tag ? `<small class="amttag">${esc(tag)}</small>` : ''}<span class="st" aria-live="polite"></span><div class="money"><span>$</span><input data-inline="${kind}" data-id="${esc(id)}" inputmode="decimal" enterkeyhint="done" aria-label="${esc(label)}" value="${cents / 100}"></div></div>`;

/** "4 items" ("1 transfer"). Paid status stays on Overview. */
export function itemCountText(d: OverviewDto, k: Category): string {
  const n = categoryItems(d, k.id).length;
  const [one, many] = k.kind === 'set_aside' ? ['transfer', 'transfers'] : ['item', 'items'];
  return `${n} ${n === 1 ? one : many}`;
}

/** One category on the Budget tab: grip, name (opens the drawer), and its amount or, itemized, its total and items. */
export function categoryRow(d: OverviewDto, k: Category): string {
  // A small note icon when the category has a note (the note itself is in the drawer and on Overview).
  const icon = k.note ? ` <svg class="noteic" viewBox="0 0 16 16" role="img" aria-label="Has a note"><path d="M3 2h7l3 3v9H3z M10 2v3h3 M5 8h6 M5 11h4"/></svg>` : '';
  const name = `<button class="bname" data-a="cat-drawer" data-id="${esc(k.id)}"><span class="clamp">${esc(k.name)}${icon}</span></button>`;
  const head = (right: string) => `<div class="brow cr">${grip('category', k.id, k.name)}${name}${right}</div>`;
  if (!k.itemized) {
    return `<div class="cat" data-cat="${esc(k.id)}">${head(inlineAmount('budget', k.id, k.budgetCents, `${k.name} planned amount`))}</div>`;
  }
  const open = !k.collapsed;
  const fold = `<button class="ktot" data-a="cat-fold" data-id="${esc(k.id)}" aria-expanded="${open}" aria-label="${open ? 'Hide' : 'Show'} the items in ${esc(k.name)}"><span><b data-ktot="${esc(k.id)}">${money(k.budgetCents)}</b><small>${esc(itemCountText(d, k))}</small></span><span class="chev" aria-hidden="true">${open ? '&#9662;' : '&#9656;'}</span></button>`;
  if (!open) return `<div class="cat" data-cat="${esc(k.id)}">${head(fold)}</div>`;
  const items = categoryItems(d, k.id)
    .map((i) => {
      const off = i.month?.status === 'not-this-month';
      // A yearly item: its amount field is the yearly charge ("Yearly"), and two lines under the
      // name: "Month 8 of 12, $15.00 a month" and "Counts through Feb 2027" or "Renews Mar 27 2027".
      const yearly = i.cadence === 'yearly';
      const amount = i.expectedMode === 'last_month' ? '<span class="amtcell muted lastm">Last month</span>' : inlineAmount('item', i.id, i.amountCents, `${i.name} ${yearly ? 'yearly charge' : 'amount'}`, yearly ? 'Yearly' : '');
      const y = yearlyLines(i, d.month.key);
      const lines = [y.term, y.when].filter(Boolean).map((t) => `<small class="ysub">${esc(t)}</small>`).join('');
      const note = lines || (off ? '<small class="muted">Not this month</small>' : '');
      return `<div class="brow cr irow"><span></span><button class="bname iname" data-a="item-edit" data-id="${esc(i.id)}"><span class="clamp">${esc(i.name)}</span>${note}</button>${amount}</div>`;
    })
    .join('');
  // Room for other spending shows once it is above $0 (it is set in the drawer). Never on money set aside.
  const room =
    k.kind !== 'set_aside' && k.extraCents > 0
      ? `<div class="brow cr irow"><span></span><span class="bname iname"><span class="clamp">Room for other spending</span></span>${inlineAmount('extra', k.id, k.extraCents, `${k.name} room for other spending`)}</div>`
      : '';
  return `<div class="cat" data-cat="${esc(k.id)}">${head(fold)}${items}${room}</div>`;
}

/** Planned this month: spending budgets, and money set aside (done transfers count toward their plan). */
export function budgetTotals(d: OverviewDto) {
  const sum = (setAside: boolean) => d.categories.filter((k) => (k.kind === 'set_aside') === setAside).reduce((s, k) => s + k.budgetCents, 0);
  return { spending: sum(false), setAside: sum(true) };
}

/**
 * The Budget tab: the plan only, no spending. A plain category is one inline amount. An itemized
 * one shows its total and item count and folds open to its items, each with an inline amount
 * (tap a name for the item editor). Tap a category name for its drawer; drag a grip to reorder.
 */
export function vBudget(d: OverviewDto): string {
  const totals = budgetTotals(d);
  const blocks = groupedCategories(d);
  return `${topBar('Budget', 'Set it once, then leave it')}
  ${vMonthBanner(d)}
  ${budgetHeadline(d, leftToBudget(d, totals))}
  ${vIncomeBudget(d)}
  <h2>Monthly amounts</h2>
  ${blocks.map((b) => groupBlock(d, b, (k) => categoryRow(d, k))).join('')}
  <div class="list"><button class="brow bitem" data-a="group-add"><span>Add group</span><span>+</span></button></div>
  <div class="list"><div class="btotal"><span>Planned this month</span><span id="ball">${money(totals.spending + totals.setAside)}</span></div></div>`;
}
